import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_URL || '/api'
const api = axios.create({ baseURL: API_BASE_URL, headers: { 'Content-Type': 'application/json' } })

api.interceptors.request.use((config) => {
  if (config.data instanceof FormData) delete config.headers['Content-Type']
  const token = localStorage.getItem('authToken')
  if (token) config.headers.Authorization = 'Bearer ' + token
  return config
})

api.interceptors.response.use(response => response, (error) => {
  if (error.response?.status === 401) {
    const requestUrl = error.config?.url || ''
    const isAuthEndpoint = requestUrl.includes('/auth/user/request-otp') || requestUrl.includes('/auth/user/verify-otp') || requestUrl.includes('/admin/auth/login')
    if (!isAuthEndpoint) {
      localStorage.removeItem('authToken')
      localStorage.removeItem('isAuthenticated')
      window.location.href = '/login'
    }
  }
  return Promise.reject(error)
})

export const authAPI = {
  userRequestOtp: (phone, options = {}) => api.post('/auth/user/request-otp', { phone, register: options.register }),
  userVerifyOtp: (phone, otp, profile = {}) => api.post('/auth/user/verify-otp', { phone, otp, ...profile }),
}

export const adminAuthAPI = {
  login: (email, password) => api.post('/admin/auth/login', { email, password }),
  logout: () => api.post('/admin/auth/logout'),
  me: () => api.get('/admin/auth/me'),
}

export const adminAPI = {
  dashboard: (params = {}) => api.get('/admin/dashboard', { params }),
  search: (params = {}) => api.get('/admin/search', { params }),
  auditLogs: (params = {}) => api.get('/admin/audit-logs', { params }),
  auditLogsResource: {
    list: (params = {}) => api.get('/admin/audit-logs', { params }),
    get: id => api.get('/admin/audit-logs/' + id),
  },
  admins: {
    list: (params = {}) => api.get('/admin/admins', { params }),
    get: id => api.get('/admin/admins/' + id),
    create: data => api.post('/admin/admins', data),
    update: (id, data) => api.put('/admin/admins/' + id, data),
    resetPassword: (id, passwordHash) => api.post('/admin/admins/' + id + '/password', { passwordHash }),
    disable: id => api.post('/admin/admins/' + id + '/disable'),
    reactivate: id => api.post('/admin/admins/' + id + '/reactivate'),
    delete: id => api.delete('/admin/admins/' + id),
  },
  mobileUsers: crud('/admin/mobile-users'),
  children: crud('/admin/children'),
  actions: crud('/admin/actions'),
  documents: crud('/admin/documents'),
  shares: { ...crud('/admin/shares'), revoke: id => api.post('/admin/shares/' + id + '/revoke') },
  schools: {
    ...crud('/admin/schools'),
    ingestPreview: data => api.post('/admin/schools/ingest/preview', data),
    ingestCommit: data => api.post('/admin/schools/ingest/commit', data),
    syncDirectory: () => api.post('/admin/schools/sync-directory'),
    cleanDirectory: () => api.post('/admin/schools/clean-directory'),
  },
  opportunities: {
    ...crud('/admin/opportunities'),
    ingestPreview: data => api.post('/admin/opportunities/ingest/preview', data),
    ingestCommit: data => api.post('/admin/opportunities/ingest/commit', data),
  },
  opportunityTypes: crud('/admin/opportunity-types'),
  notifications: {
    ...crud('/admin/notifications'),
    broadcast: data => api.post('/admin/notifications/broadcast', data),
    stats: () => api.get('/admin/notifications/stats'),
    send: data => api.post('/admin/notifications/send', data),
  },
  schoolLifeSections: crud('/admin/school-life-sections'),
  helpArticles: crud('/admin/help-articles'),
  guides: {
    ...crud('/admin/guides'),
    ingestPreview: data => api.post('/admin/guides/ingest/preview', data),
    ingestCommit: data => api.post('/admin/guides/ingest/commit', data),
  },
  appConfig: crud('/admin/app-config'),
  needContributions: crud('/admin/need-contributions'),
  agents: {
    list: () => api.get('/admin/agents'),
    get: id => api.get('/admin/agents/' + id),
    create: data => api.post('/admin/agents', data),
    resetPassword: (id, password) => api.post('/admin/agents/' + id + '/reset-password', { password }),
    update: (id, data) => api.patch('/admin/agents/' + id, data),
  },
  partners: {
    list: () => api.get('/admin/partners'),
    get: id => api.get('/admin/partners/' + id),
    create: data => api.post('/admin/partners', data),
    update: (id, data) => api.put('/admin/partners/' + id, data),
    remove: id => api.delete('/admin/partners/' + id),
    regenerateCode: id => api.post('/admin/partners/' + id + '/regenerate-code'),
    addMember: (id, data) => api.post('/admin/partners/' + id + '/members', data),
    updateMember: (id, data) => api.patch('/admin/partner-members/' + id, data),
    resetMemberPassword: (id, password) => api.post('/admin/partner-members/' + id + '/reset-password', { password }),
    removeMember: id => api.delete('/admin/partner-members/' + id),
    sections: id => api.get('/admin/partners/' + id + '/sections'),
    createSection: (id, data) => api.post('/admin/partners/' + id + '/sections', data),
    updateSection: (sid, data) => api.put('/admin/partner-sections/' + sid, data),
    removeSection: sid => api.delete('/admin/partner-sections/' + sid),
  },
}

export const uploadAPI = {
  uploadImage(file, folder = 'uploads') {
    const formData = new FormData()
    formData.append('image', file)
    formData.append('folder', folder)
    return api.post('/upload/image', formData)
  },
  uploadFile(file, folder = 'uploads') {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)
    return api.post('/upload/file', formData)
  },
}

function crud(base) {
  return {
    list: (params = {}) => api.get(base, { params }),
    get: id => api.get(base + '/' + id),
    create: data => api.post(base, data),
    update: (id, data) => api.put(base + '/' + id, data),
    delete: id => api.delete(base + '/' + id),
    restore: id => api.post(base + '/' + id + '/restore'),
  }
}

export default api
